import React, { useEffect, useState, createContext, useContext } from 'react';
import { addDays, format, isWeekend, parseISO } from 'date-fns';
// Types
export type ShiftType = 'night' | 'weekend' | 'holiday';
export type Shift = {
  id: number;
  date: string; // ISO format
  type: ShiftType;
  assignedTo: number[];
  isAvailableForTrade: boolean;
};
export interface Request {
  id: number;
  userId: number;
  shiftId: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  approvedBy?: number;
  approvedAt?: string;
}
export type Trade = {
  id: number;
  shiftId: number;
  offeredBy: number;
  takenBy: number | null;
  status: 'available' | 'completed';
};
export type Holiday = {
  id: number;
  name: string;
  date: string; // ISO format
};
export interface ActivityLog {
  id: number;
  timestamp: string;
  userId?: number;
  userName?: string;
  action: string;
  details: string;
  category: 'login' | 'request' | 'trade' | 'user' | 'holiday' | 'shift' | 'data';
}
type ShiftContextType = {
  shifts: Shift[];
  requests: Request[];
  trades: Trade[];
  holidays: Holiday[];
  activityLogs: ActivityLog[];
  addShift: (shift: Omit<Shift, 'id'>) => void;
  updateShift: (id: number, shift: Partial<Shift>) => void;
  deleteShift: (id: number) => void;
  addRequest: (request: Omit<Request, 'id' | 'status' | 'createdAt'>) => void;
  updateRequest: (id: number, status: 'approved' | 'rejected', approvedBy?: number) => void;
  addTrade: (trade: Omit<Trade, 'id' | 'status' | 'createdAt'>) => void;
  updateTrade: (id: number, status: 'accepted' | 'rejected') => void;
  addHoliday: (holiday: Omit<Holiday, 'id'>) => void;
  deleteHoliday: (id: number) => void;
  assignUserToShift: (shiftId: number, userId: number) => void;
  removeUserFromShift: (shiftId: number, userId: number) => void;
  addActivityLog: (log: Omit<ActivityLog, 'id' | 'timestamp'>) => void;
};
// Mock data
const generateMockShifts = (): Shift[] => {
  const shifts: Shift[] = [];
  const startDate = new Date();
  // Generate shifts for the next 60 days
  for (let i = 0; i < 60; i++) {
    const date = addDays(startDate, i);
    const dateStr = format(date, 'yyyy-MM-dd');
    // Add night shift for each day
    shifts.push({
      id: i * 3 + 1,
      date: dateStr,
      type: 'night',
      assignedTo: [],
      isAvailableForTrade: false
    });
    // Add weekend shifts
    if (isWeekend(date)) {
      shifts.push({
        id: i * 3 + 2,
        date: dateStr,
        type: 'weekend',
        assignedTo: [],
        isAvailableForTrade: false
      });
    }
  }
  return shifts;
};
const mockHolidays: Holiday[] = [{
  id: 1,
  name: "New Year's Day",
  date: '2026-01-01'
}, {
  id: 2,
  name: 'Easter',
  date: '2026-04-05'
}, {
  id: 3,
  name: "Mother's Day",
  date: '2026-05-10'
}, {
  id: 4,
  name: 'Memorial Day',
  date: '2026-05-25'
}, {
  id: 5,
  name: "Father's Day",
  date: '2026-06-21'
}, {
  id: 6,
  name: 'Independence Day',
  date: '2026-07-04'
}, {
  id: 7,
  name: 'Labor Day',
  date: '2026-09-07'
}, {
  id: 8,
  name: 'Thanksgiving',
  date: '2026-11-26'
}, {
  id: 9,
  name: 'Christmas',
  date: '2026-12-25'
}];
const ShiftContext = createContext<ShiftContextType | undefined>(undefined);
// Migration helper to convert old format to new format
const migrateShifts = (shifts: any[]): Shift[] => {
  return shifts.map(shift => ({
    ...shift,
    assignedTo: Array.isArray(shift.assignedTo) ? shift.assignedTo : shift.assignedTo !== null ? [shift.assignedTo] : []
  }));
};
export const ShiftProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [requests, setRequests] = useState<Request[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>(() => {
    const stored = localStorage.getItem('apnActivityLogs');
    return stored ? JSON.parse(stored) : [];
  });
  useEffect(() => {
    // Load from localStorage or use mock data
    const savedShifts = localStorage.getItem('apnShifts');
    const savedRequests = localStorage.getItem('apnRequests');
    const savedTrades = localStorage.getItem('apnTrades');
    const savedHolidays = localStorage.getItem('apnHolidays');
    if (savedShifts) {
      const parsedShifts = JSON.parse(savedShifts);
      setShifts(migrateShifts(parsedShifts));
    } else {
      const mockShifts = generateMockShifts();
      setShifts(mockShifts);
      localStorage.setItem('apnShifts', JSON.stringify(mockShifts));
    }
    if (savedRequests) {
      setRequests(JSON.parse(savedRequests));
    } else {
      setRequests([]);
    }
    if (savedTrades) {
      setTrades(JSON.parse(savedTrades));
    } else {
      setTrades([]);
    }
    if (savedHolidays) {
      setHolidays(JSON.parse(savedHolidays));
    } else {
      setHolidays(mockHolidays);
      localStorage.setItem('apnHolidays', JSON.stringify(mockHolidays));
    }
    // Add holiday shifts
    if (!savedShifts && !savedHolidays) {
      const holidayShifts = mockHolidays.map((holiday, index) => ({
        id: 10000 + index,
        date: holiday.date,
        type: 'holiday' as ShiftType,
        assignedTo: [],
        isAvailableForTrade: false
      }));
      const allShifts = [...generateMockShifts(), ...holidayShifts];
      setShifts(allShifts);
      localStorage.setItem('apnShifts', JSON.stringify(allShifts));
    }
  }, []);
  // Update localStorage when state changes
  useEffect(() => {
    localStorage.setItem('apnShifts', JSON.stringify(shifts));
  }, [shifts]);
  useEffect(() => {
    localStorage.setItem('apnRequests', JSON.stringify(requests));
  }, [requests]);
  useEffect(() => {
    localStorage.setItem('apnTrades', JSON.stringify(trades));
  }, [trades]);
  useEffect(() => {
    localStorage.setItem('apnHolidays', JSON.stringify(holidays));
  }, [holidays]);
  useEffect(() => {
    localStorage.setItem('apnActivityLogs', JSON.stringify(activityLogs));
  }, [activityLogs]);
  const addShift = (shift: Omit<Shift, 'id'>) => {
    const newShift = {
      ...shift,
      id: shifts.length > 0 ? Math.max(...shifts.map(s => s.id)) + 1 : 1
    };
    setShifts([...shifts, newShift]);
  };
  const updateShift = (id: number, shift: Partial<Shift>) => {
    setShifts(shifts.map(s => s.id === id ? {
      ...s,
      ...shift
    } : s));
  };
  const deleteShift = (id: number) => {
    setShifts(shifts.filter(s => s.id !== id));
  };
  const addRequest = (request: Omit<Request, 'id' | 'status' | 'createdAt'>) => {
    const newRequest: Request = {
      ...request,
      id: Date.now(),
      status: 'pending',
      createdAt: new Date().toISOString()
    };
    setRequests(prev => [...prev, newRequest]);
  };
  const updateRequest = (id: number, status: 'approved' | 'rejected', approvedBy?: number) => {
    setRequests(prev => prev.map(r => r.id === id ? {
      ...r,
      status,
      approvedBy,
      approvedAt: new Date().toISOString()
    } : r));
    if (status === 'approved') {
      const request = requests.find(r => r.id === id);
      if (request) {
        const shift = shifts.find(s => s.id === request.shiftId);
        if (shift) {
          // Check capacity based on shift type
          const maxCapacity = shift.type === 'weekend' ? 2 : 1;
          if (shift.assignedTo.length < maxCapacity) {
            updateShift(request.shiftId, {
              assignedTo: [...shift.assignedTo, request.userId]
            });
          }
        }
      }
    }
  };
  const addTrade = (trade: Omit<Trade, 'id' | 'status' | 'createdAt'>) => {
    const newTrade = {
      id: trades.length > 0 ? Math.max(...trades.map(t => t.id)) + 1 : 1,
      shiftId: trade.shiftId,
      offeredBy: trade.offeredBy,
      takenBy: null,
      status: 'available' as const
    };
    setTrades([...trades, newTrade]);
    updateShift(trade.shiftId, {
      isAvailableForTrade: true
    });
  };
  const updateTrade = (id: number, status: 'accepted' | 'rejected') => {
    setTrades(trades.map(t => t.id === id ? {
      ...t,
      status
    } : t));
  };
  const acceptTrade = (tradeId: number, userId: number) => {
    const trade = trades.find(t => t.id === tradeId);
    if (trade) {
      setTrades(trades.map(t => t.id === tradeId ? {
        ...t,
        takenBy: userId,
        status: 'completed'
      } : t));
      const shift = shifts.find(s => s.id === trade.shiftId);
      if (shift) {
        // Remove the user who offered the trade and add the new user
        const newAssignedTo = shift.assignedTo.filter(id => id !== trade.offeredBy);
        newAssignedTo.push(userId);
        updateShift(trade.shiftId, {
          assignedTo: newAssignedTo,
          isAvailableForTrade: false
        });
      }
    }
  };
  const addHoliday = (holiday: Omit<Holiday, 'id'>) => {
    const newHoliday = {
      ...holiday,
      id: holidays.length > 0 ? Math.max(...holidays.map(h => h.id)) + 1 : 1
    };
    setHolidays([...holidays, newHoliday]);
    // Add a shift for this holiday
    addShift({
      date: holiday.date,
      type: 'holiday',
      assignedTo: [],
      isAvailableForTrade: false
    });
  };
  const updateHoliday = (id: number, holiday: Partial<Holiday>) => {
    setHolidays(holidays.map(h => h.id === id ? {
      ...h,
      ...holiday
    } : h));
    // Update the corresponding shift if the date changed
    if (holiday.date) {
      const holidayObj = holidays.find(h => h.id === id);
      if (holidayObj) {
        const holidayShift = shifts.find(s => s.type === 'holiday' && s.date === holidayObj.date);
        if (holidayShift) {
          updateShift(holidayShift.id, {
            date: holiday.date
          });
        }
      }
    }
  };
  const deleteHoliday = (id: number) => {
    const holidayToDelete = holidays.find(h => h.id === id);
    setHolidays(holidays.filter(h => h.id !== id));
    // Delete the corresponding shift
    if (holidayToDelete) {
      const holidayShift = shifts.find(s => s.type === 'holiday' && s.date === holidayToDelete.date);
      if (holidayShift) {
        deleteShift(holidayShift.id);
      }
    }
  };
  const addActivityLog = (log: Omit<ActivityLog, 'id' | 'timestamp'>) => {
    const newLog: ActivityLog = {
      ...log,
      id: Date.now(),
      timestamp: new Date().toISOString()
    };
    setActivityLogs(prev => [newLog, ...prev]);
  };
  const assignUserToShift = (shiftId: number, userId: number) => {
    setShifts(prev => prev.map(shift => {
      if (shift.id === shiftId) {
        const maxAssignments = shift.type === 'weekend' ? 2 : 1;
        if (shift.assignedTo.length >= maxAssignments) {
          return shift;
        }
        if (shift.assignedTo.includes(userId)) {
          return shift;
        }
        return {
          ...shift,
          assignedTo: [...shift.assignedTo, userId]
        };
      }
      return shift;
    }));
  };
  const removeUserFromShift = (shiftId: number, userId: number) => {
    setShifts(prev => prev.map(shift => {
      if (shift.id === shiftId) {
        return {
          ...shift,
          assignedTo: shift.assignedTo.filter(id => id !== userId)
        };
      }
      return shift;
    }));
  };
  return <ShiftContext.Provider value={{
    shifts,
    requests,
    trades,
    holidays,
    activityLogs,
    addShift,
    updateShift,
    deleteShift,
    addRequest,
    updateRequest,
    addTrade,
    updateTrade,
    addHoliday,
    deleteHoliday,
    assignUserToShift,
    removeUserFromShift,
    addActivityLog
  }} data-id="element-458">
      {children}
    </ShiftContext.Provider>;
};
export const useShift = (): ShiftContextType => {
  const context = useContext(ShiftContext);
  if (context === undefined) {
    throw new Error('useShift must be used within a ShiftProvider');
  }
  return context;
};